import React, { useEffect, useState, useRef } from 'react';

const FullScreenToggle = () => {
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const handleFullScreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullScreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
    }, []);

    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
            });
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
    };

    return (
        <div
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
            style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                zIndex: 1000,
                opacity: isVisible ? 1 : 0,
                transition: 'opacity 0.3s ease',
                cursor: 'pointer',
                padding: '10px',
            }}
            onClick={toggleFullScreen}
        >
            <div style={{
                width: '40px',
                height: '40px',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(5px)',
                border: '1px solid rgba(255, 255, 255, 0.3)'
            }}>
                {isFullScreen ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                    </svg>
                ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                    </svg>
                )}
            </div>
        </div>
    );
};

export const ShootoutGlobalTimer = ({ calculateCurrentTime, formatTime }) => {
    const [time, setTime] = useState('00:00:00');
    const calculateCurrentTimeRef = useRef(calculateCurrentTime);
    const formatTimeRef = useRef(formatTime);

    useEffect(() => {
        calculateCurrentTimeRef.current = calculateCurrentTime;
        formatTimeRef.current = formatTime;
    });

    useEffect(() => {
        const update = () => {
            setTime(formatTimeRef.current(calculateCurrentTimeRef.current()));
        };
        update();
        const intervalId = setInterval(update, 100);
        return () => clearInterval(intervalId);
    }, []);

    return (
        <div style={{
            width: '100vw',
            height: '100vh',
            background: 'black',
            color: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 0,
            padding: 0,
            overflow: 'hidden',
            position: 'relative'
        }}>
            <FullScreenToggle />
            <span style={{
                fontSize: '12vw',
                fontWeight: 'bold',
                fontFamily: 'Arial, sans-serif',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums'
            }}>
                {time}
            </span>
        </div>
    );
};

export const ShootoutShotTimer = ({ calculateShotRemaining, gameState, calculateCurrentTime }) => {
    const [remainingSec, setRemainingSec] = useState(0);
    const [maxTime, setMaxTime] = useState(0);
    const [isWarning, setIsWarning] = useState(false);
    const calculateShotRemainingRef = useRef(calculateShotRemaining);
    const gameStateRef = useRef(gameState);
    const calculateCurrentTimeRef = useRef(calculateCurrentTime);

    useEffect(() => {
        calculateShotRemainingRef.current = calculateShotRemaining;
        gameStateRef.current = gameState;
        calculateCurrentTimeRef.current = calculateCurrentTime;
    });

    useEffect(() => {
        const update = () => {
            const remainingMs = calculateShotRemainingRef.current();
            const sec = Math.max(0, Math.ceil(remainingMs / 1000));

            const gs = gameStateRef.current;
            const activeLimitMs = Number.isFinite(gs.currentShotLimitMs) && gs.currentShotLimitMs > 0
                ? gs.currentShotLimitMs
                : (calculateCurrentTimeRef.current() >= 300000 ? 10000 : 15000);
            const max = Math.round(activeLimitMs / 1000);

            setRemainingSec(sec);
            setMaxTime(max);
            setIsWarning(sec <= 5);
        };

        update();
        const intervalId = setInterval(update, 100);
        return () => clearInterval(intervalId);
    }, []);

    return (
        <div style={{
            width: '100vw',
            height: '100vh',
            background: 'black',
            color: isWarning ? '#ff1b32ff' : '#ffffffff',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 0,
            padding: 0,
            overflow: 'hidden',
            position: 'relative'
        }}>
            <FullScreenToggle />
            <span style={{
                fontSize: '35vw',
                fontWeight: 'bold',
                fontFamily: 'Arial, sans-serif',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums'
            }}>
                {remainingSec}
            </span>
        </div>
    );
};
